// src/pages/DutyPage.tsx — admin duty scheduler at /duty.
//
// Assign / remove the DM / MOM / EM / DC / DF rota for a selected calendar MONTH
// (month pills at the top — one month at a time, current month through next year).
// DM/MOM/EM are one-per-day; DC (Duty Controller)
// and DF (Duty Fitter) are up to two per day — an EARLY (AM) and a LATE (PM) slot
// (migrations 077/078). When assigning a DC/DF you pick a shift next to each
// candidate: "All day" (the default — books both AM and PM at once) or just Early
// / Late. Each assigned person shows their own phone then the role's Common Duty
// Phone (migration 077).
//
// "Download Roster" builds the printable Word roster for the next 7 or 14 days.
//
// Backend:
//   GET    /api/duty/schedule?from=&to=  — assignments (shift, phone) + rolePhones
//   GET    /api/duty/roster?days=7|14    — printable .docx roster
//   POST   /api/duty   { date, role, contactId, shift? }   — admin (DF/DC need shift)
//   DELETE /api/duty/:id                                    — admin

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCurrentUser } from "../auth";
import { apiFetch, ApiError } from "../api";
import { C, F } from "../theme";
import {
  Avatar,
  Button,
  Card,
  Eyebrow,
  Icons,
  PermissionLoading,
  SearchInput,
  Stamp,
  TopBar,
} from "../components/ui";

type DutyRole = "DM" | "MOM" | "EM" | "DC" | "DF";
type Shift = "AM" | "PM";
const SINGLE_ROLES: ReadonlyArray<"DM" | "MOM" | "EM"> = ["DM", "MOM", "EM"];
type RosterDays = 7 | 14;
// A calendar month the list is showing. `m` is 0-based (0 = January).
interface MonthKey { y: number; m: number }
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ROLE_LABEL: Record<DutyRole, string> = {
  DM: "Duty Manager",
  MOM: "Mobile Operations",
  EM: "Event Manager",
  DC: "Duty Controller",
  DF: "Duty Fitter",
};

const ROLE_TONE: Record<DutyRole, "heather" | "brass" | "sage"> = {
  DM: "heather",
  MOM: "brass",
  EM: "sage",
  DC: "heather",
  DF: "heather",
};

function shiftLabel(s: Shift): string {
  return s === "AM" ? "EARLY (AM)" : "LATE (PM)";
}

interface Assignment {
  id: number;
  date: string; // YYYY-MM-DD
  role: DutyRole;
  shift: Shift | null;
  contact: { id: number; first: string; last: string; phone: string | null };
}

interface ScheduleResponse {
  from: string;
  to: string;
  assignments: Array<{
    id: number;
    date: string;
    role: string;
    shift: Shift | null;
    contact: { id: number; first: string; last: string; phone: string | null };
  }>;
  rolePhones: Record<string, string | null>;
}

interface ContactRow {
  id: number;
  first: string;
  last: string;
  role: string | null;
  location: string | null;
  isDM: boolean;
  isMOM: boolean;
  isEM: boolean;
  isDC: boolean;
  isDF: boolean;
}

interface ContactsListResponse {
  contacts: ContactRow[];
}

interface AssignTarget {
  date: string;
  role: DutyRole;
}

// Per-day buckets: single roles + the (up to two) Duty Fitters, EARLY first.
interface DayRoles {
  DM?: Assignment;
  MOM?: Assignment;
  EM?: Assignment;
  DC: Assignment[];
  DF: Assignment[];
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(d);
}
function formatShortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(d);
}
function isToday(iso: string): boolean {
  return iso === toIsoDate(new Date());
}
function isDutyRole(v: string): v is DutyRole {
  return v === "DM" || v === "MOM" || v === "EM" || v === "DC" || v === "DF";
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function DutyPage() {
  const navigate = useNavigate();
  const { me, loading: meLoading } = useCurrentUser();
  // Any duty-role holder (DM/MOM/EM/DC/DF) — or an admin — can view + manage the rota.
  const canDuty = Boolean(me?.isAdmin || me?.isDm || me?.isMom || me?.isEm || me?.isDc || me?.isDf);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [rolePhones, setRolePhones] = useState<Record<string, string | null>>({});
  const [opError, setOpError] = useState<string | null>(null);
  const [confirmingRemove, setConfirmingRemove] = useState<number | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);
  const [rosterDays, setRosterDays] = useState<RosterDays>(7);
  const [downloading, setDownloading] = useState(false);
  const [hidePast, setHidePast] = useState(true);            // filter: days from today
  const [searchQ, setSearchQ] = useState("");                 // name search (all months)
  const [searchResults, setSearchResults] = useState<Assignment[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [swapOpen, setSwapOpen] = useState(false);            // user-swap sheet
  const [settingsOpen, setSettingsOpen] = useState(false);    // role-phones settings sheet
  const [month, setMonth] = useState<MonthKey>(() => {
    const now = new Date();
    return { y: now.getFullYear(), m: now.getMonth() };
  });

  // Month pills: from the current month through December of NEXT year, so next
  // year's months are always selectable. One is picked at a time.
  const monthOptions = useMemo<MonthKey[]>(() => {
    const now = new Date();
    const endY = now.getFullYear() + 1;
    const out: MonthKey[] = [];
    let y = now.getFullYear();
    let m = now.getMonth();
    while (y < endY || (y === endY && m <= 11)) {
      out.push({ y, m });
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
    return out;
  }, []);

  // The list shows every day of the selected month.
  const days = useMemo(() => {
    const lastDay = new Date(month.y, month.m + 1, 0).getDate();
    return Array.from({ length: lastDay }, (_, i) => toIsoDate(new Date(month.y, month.m, i + 1)));
  }, [month]);

  const from = days[0];
  const to = days[days.length - 1];

  // "Hide past days" filter — start the month's list from today (default on).
  const todayIso = toIsoDate(new Date());
  const visibleDays = useMemo(
    () => (hidePast ? days.filter((d) => d >= todayIso) : days),
    [days, hidePast, todayIso],
  );

  // Name search across ALL months (the month view only loads one month).
  // Debounced; clearing the box returns to the month view.
  useEffect(() => {
    const q = searchQ.trim();
    if (q.length < 2) { setSearchResults(null); setSearching(false); return; }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      apiFetch<{ assignments: ScheduleResponse["assignments"] }>(`/api/duty/search?q=${encodeURIComponent(q)}`)
        .then((data) => {
          if (cancelled) return;
          setSearchResults(
            data.assignments.filter((a) => isDutyRole(a.role)).map<Assignment>((a) => ({
              id: a.id, date: a.date, role: a.role as DutyRole, shift: a.shift ?? null, contact: a.contact,
            })),
          );
        })
        .catch(() => { if (!cancelled) setSearchResults([]); })
        .finally(() => { if (!cancelled) setSearching(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [searchQ]);

  async function refreshSchedule() {
    const data = await apiFetch<ScheduleResponse>(`/api/duty/schedule?from=${from}&to=${to}`);
    setAssignments(data.assignments.filter((a) => isDutyRole(a.role)).map<Assignment>((a) => ({
      id: a.id, date: a.date, role: a.role as DutyRole, shift: a.shift ?? null, contact: a.contact,
    })));
    setRolePhones(data.rolePhones ?? {});
  }

  useEffect(() => {
    if (!canDuty) return;
    let cancelled = false;
    setLoading(true);
    apiFetch<ScheduleResponse>(`/api/duty/schedule?from=${from}&to=${to}`)
      .then((data) => {
        if (cancelled) return;
        const filtered = data.assignments
          .filter((a) => isDutyRole(a.role))
          .map<Assignment>((a) => ({
            id: a.id,
            date: a.date,
            role: a.role as DutyRole,
            shift: a.shift ?? null,
            contact: a.contact,
          }));
        setAssignments(filtered);
        setRolePhones(data.rolePhones ?? {});
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setLoadError(e instanceof Error ? e.message : "Could not load schedule");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canDuty, from, to]);

  // Build lookup: date -> { single roles + DF[] (EARLY first) }
  const byDate = useMemo(() => {
    const m: Record<string, DayRoles> = {};
    const byShift = (x: Assignment, y: Assignment) => (x.shift === "AM" ? 0 : 1) - (y.shift === "AM" ? 0 : 1);
    for (const a of assignments) {
      if (!m[a.date]) m[a.date] = { DC: [], DF: [] };
      if (a.role === "DF") m[a.date].DF.push(a);
      else if (a.role === "DC") m[a.date].DC.push(a);
      else m[a.date][a.role] = a;
    }
    for (const date of Object.keys(m)) {
      m[date].DC.sort(byShift);
      m[date].DF.sort(byShift);
    }
    return m;
  }, [assignments]);

  async function removeAssignment(id: number) {
    setRemovingId(id);
    setOpError(null);
    try {
      await apiFetch(`/api/duty/${id}`, { method: "DELETE" });
      setAssignments((prev) => prev.filter((a) => a.id !== id));
      setConfirmingRemove(null);
    } catch (e) {
      setOpError(e instanceof ApiError ? e.message : "Remove failed");
    } finally {
      setRemovingId(null);
    }
  }

  async function assignContact(target: AssignTarget, contact: ContactRow, shift?: Shift | "BOTH") {
    setOpError(null);
    if (shift === "BOTH") {
      // "All day" — book the morning (AM) and afternoon (PM) shifts in one go.
      const made: Assignment[] = [];
      try {
        for (const sh of ["AM", "PM"] as Shift[]) {
          const r = await apiFetch<{ id: number; shift: Shift | null }>("/api/duty", {
            method: "POST",
            body: JSON.stringify({ date: target.date, role: target.role, contactId: contact.id, shift: sh }),
          });
          made.push({
            id: r.id, date: target.date, role: target.role, shift: r.shift ?? sh,
            contact: { id: contact.id, first: contact.first, last: contact.last, phone: null },
          });
        }
        setAssignments((prev) => [...prev, ...made]);
        setAssignTarget(null);
      } catch (e) {
        if (made.length) setAssignments((prev) => [...prev, ...made]); // keep the shift that succeeded
        setOpError(e instanceof ApiError ? e.message : "Assign failed");
      }
      return;
    }
    try {
      const result = await apiFetch<{ id: number; shift: Shift | null }>("/api/duty", {
        method: "POST",
        body: JSON.stringify({ date: target.date, role: target.role, contactId: contact.id, shift }),
      });
      const newRow: Assignment = {
        id: result.id,
        date: target.date,
        role: target.role,
        shift: result.shift ?? shift ?? null,
        contact: { id: contact.id, first: contact.first, last: contact.last, phone: null },
      };
      setAssignments((prev) => [...prev, newRow]);
      setAssignTarget(null);
    } catch (e) {
      if (e instanceof ApiError && (e.status === 409 || e.status === 400)) {
        setOpError(e.message);
      } else {
        setOpError(e instanceof Error ? e.message : "Assign failed");
      }
    }
  }

  async function downloadRoster() {
    setOpError(null);
    setDownloading(true);
    try {
      const res = await fetch(`/api/duty/roster?days=${rosterDays}`, { credentials: "include" });
      if (!res.ok) {
        const msg = ((await res.json().catch(() => ({}))) as { error?: string }).error;
        throw new Error(msg ?? "Could not build the roster");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rosterDays === 7 ? "Weekly" : "Fortnightly"} Duty Roster ${from}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) {
      setOpError(e instanceof Error ? e.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  if (meLoading) {
    return <PermissionLoading title="Duty Scheduler" onBack={() => navigate("/apps")} />;
  }
  if (!canDuty) {
    return (
      <div>
        <TopBar title="Duty Scheduler" sub="Assign duty roles" onBack={() => navigate("/apps")} />
        <div style={{ padding: "18px 16px" }}>
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 6 }}>Duty roles only</div>
            <p style={{ fontFamily: F.body, fontSize: 14, color: C.inkSoft, fontWeight: 600, margin: "0 0 14px", lineHeight: 1.5 }}>
              The duty scheduler is open to admins and anyone holding a duty role (DM, MOM, EM, DC or DF). Ask an administrator if you need access.
            </p>
            <Button variant="secondary" onClick={() => navigate("/apps")}>Back to apps</Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopBar title="Duty Scheduler" sub="Assign duty roles" onBack={() => navigate("/apps")} />
      <div style={{ padding: "18px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <MonthPills options={monthOptions} value={month} onChange={setMonth} />
        <RosterToolbar
          rosterDays={rosterDays}
          onChangeDays={setRosterDays}
          onDownload={downloadRoster}
          downloading={downloading}
        />
        <Card padded={false}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "12px 14px" }}>
            <button
              type="button"
              onClick={() => setHidePast((v) => !v)}
              aria-pressed={hidePast}
              title="Start the list from today instead of the 1st of the month"
              style={{
                padding: "7px 14px", borderRadius: 999, fontFamily: F.body, fontWeight: 800, fontSize: 12.5,
                cursor: "pointer", whiteSpace: "nowrap",
                border: `1px solid ${hidePast ? C.brassDk : C.hairline}`,
                background: hidePast ? C.brassDk : C.paperWarm,
                color: hidePast ? C.brassGlow : C.inkSoft,
              }}
            >
              {hidePast ? "✓ Hide past days" : "Hide past days"}
            </button>
            <div style={{ flex: "1 1 200px", minWidth: 150 }}>
              <SearchInput value={searchQ} onChange={setSearchQ} placeholder="Search all months by name…" />
            </div>
            <Button variant="secondary" onClick={() => setSwapOpen(true)}>Swap users</Button>
            <Button variant="secondary" onClick={() => setSettingsOpen(true)}>Settings</Button>
          </div>
        </Card>
        {loadError && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{loadError}</div></Card>
        )}
        {opError && (
          <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{opError}</div></Card>
        )}

        {searchResults !== null ? (
          <SearchResultsView query={searchQ} searching={searching} results={searchResults} onClear={() => setSearchQ("")} />
        ) : loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} style={{ height: 180, borderRadius: 18, background: C.paperWarm, border: `1px solid ${C.hairlineLt}` }} aria-hidden="true" />
          ))
        ) : visibleDays.length === 0 ? (
          <Card><div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, textAlign: "center", padding: "22px 0", lineHeight: 1.5 }}>
            No upcoming days left this month. Turn off &ldquo;Hide past days&rdquo; to see earlier dates, or pick another month.
          </div></Card>
        ) : (
          visibleDays.map((date) => (
            <DayCard
              key={date}
              date={date}
              roles={byDate[date] ?? { DC: [], DF: [] }}
              rolePhones={rolePhones}
              confirmingRemove={confirmingRemove}
              removingId={removingId}
              onAskRemove={(id) => setConfirmingRemove(id)}
              onCancelRemove={() => setConfirmingRemove(null)}
              onConfirmRemove={removeAssignment}
              onAssign={(role) => setAssignTarget({ date, role })}
            />
          ))
        )}
      </div>

      {assignTarget && (
        <AssignSheet
          target={assignTarget}
          openShifts={
            assignTarget.role === "DF" || assignTarget.role === "DC"
              ? (() => {
                  const list = assignTarget.role === "DF" ? (byDate[assignTarget.date]?.DF ?? []) : (byDate[assignTarget.date]?.DC ?? []);
                  return { AM: !list.some((a) => a.shift === "AM"), PM: !list.some((a) => a.shift === "PM") };
                })()
              : null
          }
          onClose={() => setAssignTarget(null)}
          onPick={(c, shift) => assignContact(assignTarget, c, shift)}
        />
      )}

      {swapOpen && (
        <SwapSheet onClose={() => setSwapOpen(false)} onDone={refreshSchedule} />
      )}
      {settingsOpen && (
        <SettingsSheet
          initial={rolePhones}
          onClose={() => setSettingsOpen(false)}
          onSaved={(rp) => setRolePhones(rp)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Month selector — one horizontally-scrollable pill per month (current month
// through December of next year). Single-select: picking one shows that month.
// ---------------------------------------------------------------------------

function MonthPills({ options, value, onChange }: {
  options: MonthKey[];
  value: MonthKey;
  onChange: (v: MonthKey) => void;
}) {
  return (
    <Card padded={false}>
      <div
        role="radiogroup"
        aria-label="Select month"
        style={{ display: "flex", gap: 8, overflowX: "auto", padding: "12px 14px", WebkitOverflowScrolling: "touch" }}
      >
        {options.map((o) => {
          const sel = o.y === value.y && o.m === value.m;
          return (
            <button
              key={`${o.y}-${o.m}`}
              type="button"
              role="radio"
              aria-checked={sel}
              onClick={() => onChange(o)}
              aria-label={`${MONTH_ABBR[o.m]} ${o.y}`}
              style={{
                flex: "0 0 auto",
                padding: "8px 14px",
                borderRadius: 999,
                border: `1px solid ${sel ? C.brassDk : C.hairline}`,
                background: sel ? C.brassDk : C.paperWarm,
                color: sel ? C.brassGlow : C.inkSoft,
                fontFamily: F.body,
                fontWeight: 800,
                fontSize: 13,
                letterSpacing: 0.2,
                cursor: sel ? "default" : "pointer",
                whiteSpace: "nowrap",
              }}
            >
              {MONTH_ABBR[o.m]} &rsquo;{String(o.y).slice(2)}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Roster toolbar — Download Roster button + 7/14-day window pill
// ---------------------------------------------------------------------------

function RosterToolbar({ rosterDays, onChangeDays, onDownload, downloading }: {
  rosterDays: RosterDays;
  onChangeDays: (d: RosterDays) => void;
  onDownload: () => void;
  downloading: boolean;
}) {
  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, letterSpacing: -0.2 }}>Download Roster</div>
          <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 12.5, color: C.inkMute, marginTop: 2 }}>
            A printable Word roster for the next {rosterDays} days, from today.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div role="radiogroup" aria-label="Roster length" style={{ display: "inline-flex", background: C.paperWarm, border: `1px solid ${C.hairline}`, borderRadius: 999, padding: 3, gap: 2 }}>
            {([7, 14] as RosterDays[]).map((n) => {
              const sel = rosterDays === n;
              return (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  onClick={() => onChangeDays(n)}
                  style={{
                    padding: "8px 16px", borderRadius: 999, border: "none",
                    cursor: sel ? "default" : "pointer",
                    background: sel ? C.brassDk : "transparent",
                    color: sel ? C.brassGlow : C.inkSoft,
                    fontFamily: F.body, fontWeight: 800, fontSize: 13, letterSpacing: 0.2,
                  }}
                >
                  {n} days
                </button>
              );
            })}
          </div>
          <Button onClick={onDownload} disabled={downloading} aria-label="Download the duty roster as a Word document">
            <Icons.Doc s={18} c={C.brassGlow} /> {downloading ? "Building…" : "Download Roster"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DayCard
// ---------------------------------------------------------------------------

interface DayCardProps {
  date: string;
  roles: DayRoles;
  rolePhones: Record<string, string | null>;
  confirmingRemove: number | null;
  removingId: number | null;
  onAskRemove: (id: number) => void;
  onCancelRemove: () => void;
  onConfirmRemove: (id: number) => void;
  onAssign: (role: DutyRole) => void;
}

function DayCard({
  date,
  roles,
  rolePhones,
  confirmingRemove,
  removingId,
  onAskRemove,
  onCancelRemove,
  onConfirmRemove,
  onAssign,
}: DayCardProps) {
  const todayBadge = isToday(date);
  return (
    <Card padded={false}>
      <div style={{ padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px dashed ${C.hairlineLt}` }}>
        <div>
          <Eyebrow>{todayBadge ? "Today" : formatShortDate(date)}</Eyebrow>
          <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 700, color: C.ink, letterSpacing: -0.2, marginTop: 2 }}>{formatLongDate(date)}</div>
        </div>
        {todayBadge && <Stamp tone="brass">Today</Stamp>}
      </div>
      <div>
        {/* Single-occupant roles */}
        {SINGLE_ROLES.map((role, idx) => {
          const a = roles[role];
          return a
            ? (
              <AssignedRow
                key={role} role={role} a={a} first={idx === 0}
                commonPhone={rolePhones[role] ?? null}
                confirming={confirmingRemove === a.id} removing={removingId === a.id}
                onAskRemove={onAskRemove} onCancelRemove={onCancelRemove} onConfirmRemove={onConfirmRemove}
                dateLabel={formatShortDate(date)}
              />
            )
            : <EmptyRow key={role} role={role} first={idx === 0} onAssign={() => onAssign(role)} />;
        })}

        {/* Duty Controller — up to two (EARLY first, then LATE) */}
        {roles.DC.map((a) => (
          <AssignedRow
            key={a.id} role="DC" a={a} first={false}
            commonPhone={rolePhones.DC ?? null}
            confirming={confirmingRemove === a.id} removing={removingId === a.id}
            onAskRemove={onAskRemove} onCancelRemove={onCancelRemove} onConfirmRemove={onConfirmRemove}
            dateLabel={formatShortDate(date)}
          />
        ))}
        {roles.DC.length < 2 && (
          <EmptyRow role="DC" first={false} onAssign={() => onAssign("DC")} label={roles.DC.length === 0 ? "Assign Duty Controller" : "Assign a second Duty Controller"} />
        )}

        {/* Duty Fitter — up to two (EARLY first, then LATE) */}
        {roles.DF.map((a) => (
          <AssignedRow
            key={a.id} role="DF" a={a} first={false}
            commonPhone={rolePhones.DF ?? null}
            confirming={confirmingRemove === a.id} removing={removingId === a.id}
            onAskRemove={onAskRemove} onCancelRemove={onCancelRemove} onConfirmRemove={onConfirmRemove}
            dateLabel={formatShortDate(date)}
          />
        ))}
        {roles.DF.length < 2 && (
          <EmptyRow role="DF" first={false} onAssign={() => onAssign("DF")} label={roles.DF.length === 0 ? "Assign Duty Fitter" : "Assign a second Duty Fitter"} />
        )}
      </div>
    </Card>
  );
}

function PhoneLines({ personal, common }: { personal: string | null; common: string | null }) {
  if (!personal && !common) return null;
  return (
    <div style={{ marginTop: 3 }}>
      {personal && (
        <a href={`tel:${personal}`} style={{ display: "block", fontFamily: F.mono, fontSize: 12.5, fontWeight: 700, color: C.brassDk, textDecoration: "none" }}>{personal}</a>
      )}
      {common && (
        <a href={`tel:${common}`} style={{ display: "block", fontFamily: F.mono, fontSize: 11.5, fontWeight: 700, color: C.inkSoft, textDecoration: "none" }}>Common Duty Phone: {common}</a>
      )}
    </div>
  );
}

function AssignedRow({
  role, a, first, commonPhone, confirming, removing, onAskRemove, onCancelRemove, onConfirmRemove, dateLabel,
}: {
  role: DutyRole;
  a: Assignment;
  first: boolean;
  commonPhone: string | null;
  confirming: boolean;
  removing: boolean;
  onAskRemove: (id: number) => void;
  onCancelRemove: () => void;
  onConfirmRemove: (id: number) => void;
  dateLabel: string;
}) {
  return (
    <div style={{ padding: "10px 16px", display: "flex", flexDirection: "column", gap: 8, borderTop: first ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <Avatar first={a.contact.first} last={a.contact.last} size={38} tone={ROLE_TONE[role]} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2, flexWrap: "wrap" }}>
            <Stamp tone={ROLE_TONE[role]}>{role}</Stamp>
            {a.shift && <Stamp tone="brass">{shiftLabel(a.shift)}</Stamp>}
            <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11, color: C.inkMute, fontWeight: 600 }}>{ROLE_LABEL[role]}</span>
          </div>
          <div style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, lineHeight: 1.15, letterSpacing: -0.2 }}>
            {a.contact.first} {a.contact.last}
          </div>
          <PhoneLines personal={a.contact.phone} common={commonPhone} />
        </div>
        <button
          type="button"
          onClick={() => onAskRemove(a.id)}
          aria-label={`Remove ${a.contact.first} as ${role}`}
          disabled={removing}
          style={{ width: 38, height: 38, borderRadius: 11, background: C.signalBg, border: `1px solid rgba(184,58,43,0.22)`, display: "flex", alignItems: "center", justifyContent: "center", cursor: removing ? "default" : "pointer", flexShrink: 0 }}
        >
          <Icons.Close s={14} c={C.signalDk} />
        </button>
      </div>
      {confirming && (
        <div style={{ padding: 12, borderRadius: 12, background: C.signalBg, border: `1px solid rgba(184,58,43,0.22)` }}>
          <div style={{ fontFamily: F.serif, fontSize: 14, fontWeight: 700, color: C.signalDk, marginBottom: 10, lineHeight: 1.4 }}>
            Remove {a.contact.first} {a.contact.last} as {role}{a.shift ? ` (${shiftLabel(a.shift)})` : ""} on {dateLabel}?
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Button variant="danger" disabled={removing} onClick={() => onConfirmRemove(a.id)}>{removing ? "Removing..." : "Yes, remove"}</Button>
            <Button variant="ghost" onClick={onCancelRemove}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyRow({ role, first, onAssign, label }: { role: DutyRole; first: boolean; onAssign: () => void; label?: string }) {
  return (
    <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, borderTop: first ? "none" : `1px solid ${C.hairlineLt}` }}>
      <div style={{ width: 38, height: 38, borderRadius: 11, background: C.paperWarm, border: `1px dashed ${C.hairline}`, flexShrink: 0 }} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
          <Stamp tone={ROLE_TONE[role]}>{role}</Stamp>
          <span style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 11, color: C.inkMute, fontWeight: 600 }}>{ROLE_LABEL[role]}</span>
        </div>
        <div style={{ fontFamily: F.serif, fontStyle: "italic", fontSize: 15, color: C.inkMute, fontWeight: 600 }}>{label ?? "Unassigned"}</div>
      </div>
      <button
        type="button"
        onClick={onAssign}
        aria-label={`Assign ${role}`}
        style={{ width: 38, height: 38, borderRadius: 11, background: C.brassBg, border: `1px solid ${C.brassLt}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}
      >
        <Icons.Plus s={16} c={C.brassDk} />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assign sheet
// ---------------------------------------------------------------------------

function AssignSheet({
  target,
  openShifts,
  onClose,
  onPick,
}: {
  target: AssignTarget;
  openShifts: { AM: boolean; PM: boolean } | null;
  onClose: () => void;
  onPick: (c: ContactRow, shift?: Shift | "BOTH") => void;
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    apiFetch<ContactsListResponse>("/api/contacts")
      .then((data) => { if (!cancelled) setContacts(data.contacts); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : "Could not load contacts"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const eligible = useMemo(() => {
    const flagKey: keyof Pick<ContactRow, "isDM" | "isMOM" | "isEM" | "isDC" | "isDF"> =
      target.role === "DM" ? "isDM"
      : target.role === "MOM" ? "isMOM"
      : target.role === "EM" ? "isEM"
      : target.role === "DC" ? "isDC"
      : "isDF";
    const filtered = contacts.filter((c) => Boolean(c[flagKey]));
    if (!q.trim()) return filtered;
    const needle = q.trim().toLowerCase();
    return filtered.filter(
      (c) =>
        c.first.toLowerCase().includes(needle) ||
        c.last.toLowerCase().includes(needle) ||
        (c.role ?? "").toLowerCase().includes(needle) ||
        (c.location ?? "").toLowerCase().includes(needle),
    );
  }, [contacts, target.role, q]);

  const isShift = target.role === "DF" || target.role === "DC";

  return (
    <div style={{ position: "fixed", inset: 0, background: C.paper, zIndex: 80, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <TopBar title={`Assign ${target.role}`} sub={`for ${formatLongDate(target.date)}`} onBack={onClose} />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 12 }}>
        {isShift && (
          <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5 }}>
            <strong>All day</strong> books both the morning and afternoon — the usual choice. Pick <em>Early</em> or <em>Late</em> only when two people split the day.{openShifts?.AM ? "" : " Early (AM) is taken."}{openShifts?.PM ? "" : " Late (PM) is taken."}
          </div>
        )}
        <SearchInput value={q} onChange={setQ} placeholder={`Search eligible ${target.role}s...`} />

        {error && <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div></Card>}

        {loading ? (
          <Card><div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, textAlign: "center", padding: "22px 0" }}>Loading contacts...</div></Card>
        ) : eligible.length === 0 ? (
          <Card><div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, textAlign: "center", padding: "22px 0", lineHeight: 1.5 }}>
            {q.trim() ? `No eligible contacts match "${q}"` : `No eligible contacts. Flag people as ${target.role} in User Access.`}
          </div></Card>
        ) : (
          <Card padded={false}>
            {eligible.map((c, i) => (
              <div
                key={c.id}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}
              >
                <Avatar first={c.first} last={c.last} size={42} tone={ROLE_TONE[target.role]} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: F.serif, fontSize: 16, fontWeight: 700, color: C.ink, letterSpacing: -0.2 }}>{c.first} {c.last}</div>
                  <div style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 2 }}>{[c.role, c.location].filter(Boolean).join(" · ") || "—"}</div>
                </div>
                {isShift ? (
                  <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <ShiftPill label="All day" primary disabled={!(openShifts?.AM && openShifts?.PM)} onClick={() => onPick(c, "BOTH")} />
                    <ShiftPill label="Early" disabled={!openShifts?.AM} onClick={() => onPick(c, "AM")} />
                    <ShiftPill label="Late" disabled={!openShifts?.PM} onClick={() => onPick(c, "PM")} />
                  </div>
                ) : (
                  <button type="button" onClick={() => onPick(c)} aria-label={`Assign ${c.first} ${c.last}`} style={{ border: "none", background: "transparent", cursor: "pointer", flexShrink: 0 }}>
                    <Icons.Chev s={18} c={C.inkMute} />
                  </button>
                )}
              </div>
            ))}
          </Card>
        )}
      </div>
    </div>
  );
}

function ShiftPill({ label, disabled, primary, onClick }: { label: string; disabled?: boolean; primary?: boolean; onClick: () => void }) {
  const solid = primary && !disabled;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        fontFamily: F.body,
        fontWeight: 800,
        fontSize: 11,
        letterSpacing: 0.3,
        cursor: disabled ? "not-allowed" : "pointer",
        border: `1px solid ${disabled ? C.hairline : solid ? C.brassDk : C.brassLt}`,
        background: disabled ? C.paperWarm : solid ? C.brassDk : C.brassBg,
        color: disabled ? C.inkMute : solid ? C.brassGlow : C.brassDk,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Name search results — a person's upcoming duties across ALL months.
// ---------------------------------------------------------------------------
function SearchResultsView({ query, searching, results, onClear }: {
  query: string;
  searching: boolean;
  results: Assignment[];
  onClear: () => void;
}) {
  return (
    <>
      <Card padded={false}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 14px" }}>
          <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft }}>
            {searching ? "Searching…" : `${results.length} ${results.length === 1 ? "duty" : "duties"} matching “${query.trim()}” · all months`}
          </div>
          <button type="button" onClick={onClear} style={{ border: "none", background: "transparent", cursor: "pointer", fontFamily: F.body, fontWeight: 800, fontSize: 12.5, color: C.brassDk }}>Clear</button>
        </div>
      </Card>
      {!searching && results.length === 0 ? (
        <Card><div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, textAlign: "center", padding: "22px 0" }}>No upcoming duties found for “{query.trim()}”.</div></Card>
      ) : results.length > 0 ? (
        <Card padded={false}>
          {results.map((a, i) => (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", borderTop: i === 0 ? "none" : `1px solid ${C.hairlineLt}` }}>
              <Avatar first={a.contact.first} last={a.contact.last} size={40} tone={ROLE_TONE[a.role]} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink, letterSpacing: -0.2 }}>{a.contact.first} {a.contact.last}</div>
                <div style={{ fontSize: 12, color: C.inkSoft, fontWeight: 600, marginTop: 2 }}>{ROLE_LABEL[a.role]}{a.shift ? ` · ${shiftLabel(a.shift)}` : ""}</div>
              </div>
              <div style={{ fontFamily: F.body, fontSize: 12.5, fontWeight: 800, color: C.inkSoft, textAlign: "right", whiteSpace: "nowrap" }}>{formatShortDate(a.date)}</div>
            </div>
          ))}
        </Card>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// Native select field (used by the swap sheet).
// ---------------------------------------------------------------------------
function SelectField({ label, value, onChange, options, placeholder }: {
  label: string;
  value: number | "";
  onChange: (v: number | "") => void;
  options: ContactRow[];
  placeholder: string;
}) {
  return (
    <div>
      <div style={{ fontFamily: F.serif, fontStyle: "italic", fontWeight: 700, fontSize: 13.5, color: C.brassDk, marginBottom: 4 }}>{label}</div>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
        style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.hairline}`, fontFamily: F.body, fontSize: 14, color: C.ink, background: C.paper, boxSizing: "border-box" }}
      >
        <option value="">{placeholder}</option>
        {options.map((c) => (
          <option key={c.id} value={c.id}>{c.first} {c.last}{c.role ? ` — ${c.role}` : ""}</option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Swap sheet — reassign one person's future duties to another (all months).
// ---------------------------------------------------------------------------
function SwapSheet({ onClose, onDone }: { onClose: () => void; onDone: () => Promise<void> | void }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [fromId, setFromId] = useState<number | "">("");
  const [toId, setToId] = useState<number | "">("");
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<{ swapped: number; skipped: number; fromName: string; toName: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<ContactsListResponse>("/api/contacts")
      .then((d) => { if (!cancelled) setContacts(d.contacts.filter((x) => x.isDM || x.isMOM || x.isEM || x.isDC || x.isDF)); })
      .catch((e: unknown) => { if (!cancelled) setError(e instanceof Error ? e.message : "Could not load people"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sorted = useMemo(
    () => [...contacts].sort((a, b) => `${a.last}${a.first}`.localeCompare(`${b.last}${b.first}`, "en-GB", { sensitivity: "base" })),
    [contacts],
  );

  const sameChosen = fromId !== "" && toId !== "" && fromId === toId;

  async function apply() {
    if (fromId === "" || toId === "" || sameChosen) return;
    setApplying(true); setError(null);
    try {
      const r = await apiFetch<{ swapped: number; skipped: number; fromName: string; toName: string }>("/api/duty/swap", {
        method: "POST",
        body: JSON.stringify({ fromContactId: fromId, toContactId: toId }),
      });
      setResult(r);
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Swap failed");
    } finally {
      setApplying(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.paper, zIndex: 80, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <TopBar title="Swap users" sub="Reassign one person's future duties to another" onBack={onClose} />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        {error && <Card><div style={{ color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div></Card>}
        {result ? (
          <Card>
            <div style={{ fontFamily: F.serif, fontSize: 17, fontWeight: 700, color: C.ink, marginBottom: 8 }}>Swap complete</div>
            <p style={{ fontFamily: F.body, fontSize: 13.5, color: C.inkSoft, fontWeight: 600, lineHeight: 1.5, margin: "0 0 14px" }}>
              Moved <strong>{result.swapped}</strong> future {result.swapped === 1 ? "duty" : "duties"} from {result.fromName} to {result.toName}.
              {result.skipped > 0 ? ` ${result.skipped} ${result.skipped === 1 ? "slot was" : "slots were"} skipped because ${result.toName} was already on duty at that time.` : ""}
            </p>
            <Button onClick={onClose}>Done</Button>
          </Card>
        ) : loading ? (
          <Card><div style={{ fontFamily: F.serif, fontStyle: "italic", color: C.inkMute, textAlign: "center", padding: "22px 0" }}>Loading people…</div></Card>
        ) : (
          <Card>
            <p style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, margin: "0 0 16px" }}>
              This reassigns <strong>every duty from today onwards</strong> held by the first person to the second, across all months. Past duties are left unchanged, and any slot the replacement already covers is skipped.
            </p>
            <SelectField label="Person to swap" value={fromId} onChange={setFromId} options={sorted} placeholder="Choose a person…" />
            <div style={{ height: 12 }} />
            <SelectField label="Swap to" value={toId} onChange={setToId} options={sorted} placeholder="Choose the replacement…" />
            {sameChosen && <div style={{ marginTop: 8, color: C.signalDk, fontFamily: F.body, fontSize: 12.5, fontWeight: 700 }}>Choose two different people.</div>}
            <div style={{ marginTop: 16 }}>
              <Button onClick={apply} disabled={fromId === "" || toId === "" || sameChosen || applying}>{applying ? "Swapping…" : "Apply swap"}</Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Settings sheet — common per-role duty phones (moved here from User Access).
// Editable by anyone with Duty App access.
// ---------------------------------------------------------------------------
function SettingsSheet({ initial, onClose, onSaved }: {
  initial: Record<string, string | null>;
  onClose: () => void;
  onSaved: (rp: Record<string, string | null>) => void;
}) {
  const ROLES: DutyRole[] = ["DM", "MOM", "EM", "DC", "DF"];
  const [vals, setVals] = useState<Record<string, string>>(() => {
    const o: Record<string, string> = {};
    for (const r of ROLES) o[r] = initial[r] ?? "";
    return o;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  async function save() {
    setSaving(true); setError(null); setSavedMsg(null);
    try {
      const result: Record<string, string | null> = { ...initial };
      for (const r of ROLES) {
        const next = vals[r].trim();
        const prev = (initial[r] ?? "").trim();
        if (next !== prev) {
          await apiFetch("/api/duty/role-phones", { method: "PUT", body: JSON.stringify({ role: r, phone: next }) });
          result[r] = next === "" ? null : next;
        }
      }
      onSaved(result);
      setSavedMsg("Saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: C.paper, zIndex: 80, overflowY: "auto", display: "flex", flexDirection: "column" }}>
      <TopBar title="Duty settings" sub="Common duty phones" onBack={onClose} />
      <div style={{ padding: "14px 16px 24px", display: "flex", flexDirection: "column", gap: 14 }}>
        <Card>
          <p style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, lineHeight: 1.5, margin: "0 0 16px" }}>
            One shared number per duty role, shown on the roster after each person's own (or labelled “Common Duty Phone” if they have none). Editing here changes it for everyone holding that role.
          </p>
          {error && <div style={{ marginBottom: 12, color: C.signalDk, fontFamily: F.body, fontWeight: 700 }}>{error}</div>}
          {ROLES.map((r) => (
            <div key={r} style={{ marginBottom: 12 }}>
              <div style={{ fontFamily: F.serif, fontStyle: "italic", fontWeight: 700, fontSize: 13.5, color: C.brassDk, marginBottom: 4 }}>{ROLE_LABEL[r]} ({r})</div>
              <input
                value={vals[r]}
                onChange={(e) => setVals((v) => ({ ...v, [r]: e.target.value }))}
                placeholder="Common duty phone (optional)"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 12, border: `1px solid ${C.hairline}`, fontFamily: F.body, fontSize: 14, color: C.ink, background: C.paper, boxSizing: "border-box" }}
              />
            </div>
          ))}
          {savedMsg && <div style={{ margin: "4px 0 12px", color: C.sageDk, fontFamily: F.body, fontWeight: 800, fontSize: 13 }}>{savedMsg}</div>}
          <Button onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>
        </Card>
      </div>
    </div>
  );
}
